import {
  Box,
  Card,
  CardSection,
  Center,
  Drawer,
  Loader,
  Title
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSearchParams } from 'react-router'
import { ZOHO_LICENSE_UPGRADE_FORM_URL } from '@/config/main'
import { useGetWorkspaceUsage } from '@/queries/workspaceQueries'
import { showToast } from '@/theme/notifications'
import { ZohoEmbeddedForm } from '../support/ZohoEmbeddedForm'
import { useWorkspace } from '../workspaces/WorkspaceContext'
import { BillingSummary } from './BillingSummary/BillingSummary'
import { LicenseAddons } from './LicenseAddons/LicenseAddons'
import { LicenseFeatures } from './LicenseFeatures/LicenseFeatures'
import { LicenseInfo } from './LicenseInfo/LicenseInfo'
import { UsageSummary } from './UsageSummary/UsageSummary'

const OPEN_DRAWER_PARAM = 'openUpgrade'

export const SubscriptionPage = () => {
  const intl = useIntl()
  const [
    isUpgradeDrawerOpened,
    { open: openUpgradeDrawer, close: closeUpgradeDrawer }
  ] = useDisclosure(false)

  const { subscription } = useWorkspace()

  const { data: workspaceUsage, isLoading: isLoadingUsage } =
    useGetWorkspaceUsage()

  const license = subscription.ordered_items.find(
    (item) => item.product.product_category === 'license'
  )

  const extraStreams = subscription.ordered_items.find(
    (item) => item.product.product_category === 'streams'
  )

  const extraVlmStreams = subscription.ordered_items.find(
    (item) => item.product.product_category === 'vlm streams'
  )

  const vlmAgents = subscription.ordered_items.find(
    (item) => item.product.product_category === 'vlm agents'
  )

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.get(OPEN_DRAWER_PARAM) === 'true') {
      openUpgradeDrawer()
    }
  }, [searchParams, openUpgradeDrawer])

  const removeParamAndCloseDrawer = () => {
    closeUpgradeDrawer()
    searchParams.delete(OPEN_DRAWER_PARAM)
    setSearchParams(searchParams)
  }

  const handleZohoFormSubmit = () => {
    showToast(
      intl.formatMessage({ id: 'subscriptions.upgrade.submitted' }),
      'success'
    )

    removeParamAndCloseDrawer()
  }

  if (!license) {
    return null
  }

  return (
    <>
      <Title order={3} mb="sm">
        <FormattedMessage id="subscriptions.currentLicense" />
      </Title>

      <Card mb="xl" padding="lg" withBorder>
        <CardSection py="md" inheritPadding withBorder>
          <LicenseInfo
            licenseName={license.product.product_name}
            price={license.product.unit_price}
            currency={license.product.price_currency}
            billingPeriod={license.product.billing_period}
            onUpgrade={openUpgradeDrawer}
          />
        </CardSection>

        <CardSection py="md" inheritPadding>
          <LicenseFeatures license={license.product} />
        </CardSection>
      </Card>

      {((extraStreams && extraStreams.quantity > 0) ||
        (extraVlmStreams && extraVlmStreams.quantity > 0) ||
        vlmAgents) && (
        <Box mb="xl">
          <Title order={3} mb="sm">
            <FormattedMessage id="subscriptions.addons.title" />
          </Title>

          <LicenseAddons
            extraStreams={extraStreams?.quantity}
            extraVlmStreams={extraVlmStreams?.quantity}
            hasVlmAgents={!!vlmAgents}
          />
        </Box>
      )}

      <Box mb="xl">
        <Title order={3} mb="sm">
          <FormattedMessage id="subscriptions.billingSummary" />
        </Title>

        <BillingSummary
          billingContactEmail={subscription.user.email}
          startDate={subscription.start_date}
          endDate={subscription.end_date}
          status={subscription.status}
        />
      </Box>

      <Title order={3} mb="sm">
        <FormattedMessage id="subscriptions.usage.title" />
      </Title>

      <Card padding={48} withBorder>
        <CardSection py={32} inheritPadding withBorder>
          {isLoadingUsage && (
            <Center mih={120}>
              <Loader size="sm" />
            </Center>
          )}

          {workspaceUsage && (
            <UsageSummary featureUsageSummary={workspaceUsage} />
          )}
        </CardSection>
      </Card>

      <Drawer
        opened={isUpgradeDrawerOpened}
        position="right"
        size="60rem"
        title={<FormattedMessage id="subscriptions.upgradeLicense" />}
        onClose={removeParamAndCloseDrawer}
      >
        {isUpgradeDrawerOpened && (
          <ZohoEmbeddedForm
            formUrl={ZOHO_LICENSE_UPGRADE_FORM_URL}
            onSubmit={handleZohoFormSubmit}
          />
        )}
      </Drawer>
    </>
  )
}
